<script setup lang="ts">
import { computed, watch } from 'vue'
import VueApexCharts from 'vue3-apexcharts'
import { useManagementStore } from '@/store/management'

const store = useManagementStore()

const area = computed(() => store.activeArea)
const data = computed(() => (area.value ? store.areaDailyRuntime(area.value.id) : null))
const days = computed(() => data.value?.days ?? [])

// Rótulo curto no eixo X: dia/mês (ex.: 01/jan).
const categories = computed(() =>
  days.value.map((d) => {
    const date = new Date(`${d.date}T00:00:00`)
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }).replace('.', '')
  }),
)

// Horas (1 casa) para casar com a planilha do cliente.
const toHours = (minutes: number) => Math.round((minutes / 60) * 10) / 10

// Parada embaixo (vermelho) e rodando em cima (verde) — empilhados como na planilha.
const series = computed(() => [
  { name: 'Tempo parada', data: days.value.map((d) => toHours(d.off_minutes)) },
  { name: 'Tempo Funcionando', data: days.value.map((d) => toHours(d.on_minutes)) },
])

const options = computed(() => ({
  chart: { type: 'bar', stacked: true, toolbar: { show: false }, fontFamily: 'inherit' },
  colors: ['#EF4444', '#10B981'], // parada vermelho (base), rodando verde (topo)
  plotOptions: {
    bar: {
      columnWidth: '70%',
      borderRadius: 2,
      // Total de horas no topo de cada coluna.
      dataLabels: { total: { enabled: true, style: { fontSize: '10px', fontWeight: 600, color: '#637381' }, formatter: (v: number) => `${Number(v).toFixed(0)}h` } },
    },
  },
  // Rótulo dentro de cada segmento: horas de parada (vermelho) e de funcionamento (verde).
  dataLabels: {
    enabled: true,
    formatter: (v: number) => (v ? `${Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}h` : ''),
    style: { fontSize: '10px', fontWeight: 600, colors: ['#fff'] },
    dropShadow: { enabled: true, top: 0, left: 0, blur: 1, opacity: 0.35 },
  },
  legend: { position: 'top', horizontalAlign: 'center' },
  xaxis: { categories: categories.value, labels: { rotate: -45, style: { fontSize: '10px' } } },
  yaxis: { max: 24, labels: { formatter: (v: number) => `${Number(v).toFixed(0)}h` } },
  tooltip: { y: { formatter: (v: number) => `${Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} h` } },
}))

// Auto-busca ao trocar a área ativa ou o período.
watch(
  () => [area.value?.id, store.periodQuery] as const,
  ([areaId]) => {
    if (areaId) store.fetchDailyRuntime(areaId)
  },
  { immediate: true },
)
</script>

<template>
  <div class="border border-stroke bg-white p-4">
    <h4 class="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">
      Tempo de parada × Tempo rodando (diário)
    </h4>

    <div v-if="!area" class="flex min-h-[260px] items-center justify-center text-sm text-slate-400">
      Selecione uma área para visualizar o gráfico diário.
    </div>
    <div v-else-if="!days.length" class="flex min-h-[260px] items-center justify-center text-sm text-slate-400">
      Sem registros de parada para o período.
    </div>
    <VueApexCharts v-else type="bar" height="320" :options="options" :series="series" />
  </div>
</template>
